/**
 * Invalidating the caches that hold a study's CONTENT — its series and its
 * instance rows — after something mutated it.
 *
 * There are TWO roots, and that is the whole reason this exists. The
 * authenticated viewer opens on the summary payload, cached under
 * `['study-summary', id, caseId]`; the share view, the delete modal's own
 * lookup and every non-viewer surface read the fat payload under
 * `['study', id, …]`. React Query matches a query key from its FIRST element
 * outwards, so `['study', id]` cannot reach the summary root however much of
 * the rest lines up — it is a different tree.
 *
 * A mutation that invalidates only the fat root therefore succeeds, reports
 * success, and leaves the reader looking at the pre-mutation study: the
 * burned-in "Annotated" series does not appear, the deleted series does not
 * disappear. Nothing surfaces the mistake, because the ingest poll eventually
 * moves a count and refetches anyway — up to a minute later, on its idle
 * cadence. So both roots are invalidated from one place rather than at each
 * call site, where the pair drifted apart the moment the viewer's key changed.
 */
/**
 * Mark every cached read of `studyId`'s content stale, under both roots.
 *
 * Awaitable: React Query resolves once the active refetches it triggered have
 * settled, which is what lets a caller show its success toast after the viewer
 * is actually holding the new payload.
 */
export async function invalidateStudyContent(queryClient, studyId) {
    await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['study', studyId] }),
        queryClient.invalidateQueries({ queryKey: ['study-summary', studyId] }),
    ]);
}
